export interface WalkthroughStep {
  title: string;
  body: string;
  targetText?: string;
}

export interface HelpGuide {
  id: string;
  title: string;
  summary: string;
  keywords: string[];
  path: RegExp;
  steps: WalkthroughStep[];
}

export const HELP_GUIDES: HelpGuide[] = [
  {
    id: 'analyse-data',
    title: 'Analyse and plot compound data',
    summary: 'Build a scatter plot, colour compounds and inspect selected structures.',
    keywords: ['analyse', 'analysis', 'plot', 'graph', 'scatter', 'regression', 'statistics', 'datawarrior'],
    path: /^\/chemistry\/file\/[^/]+\/editor/,
    steps: [
      { title: 'Open Analyse', body: 'Select Statistics in the file toolbar.', targetText: 'Statistics' },
      { title: 'Choose axes', body: 'Select any imported numeric column for X and Y in Scatter Plot.' },
      { title: 'Style the points', body: 'Use Colour for a categorical column such as Series. Size and Shape can use other imported fields.' },
      { title: 'Add scientific overlays', body: 'Enable y = x, Linear fit, marginal histograms or editable thresholds as required.' },
      { title: 'Inspect compounds', body: 'Select or lasso points. The compound table updates with the selection; choose a compound name to open its details.' },
    ],
  },
  {
    id: 'import-data',
    title: 'Import chemistry data',
    summary: 'Upload a structure-linked file and map its fields safely.',
    keywords: ['import', 'upload', 'csv', 'xlsx', 'sdf', 'dwar', 'file', 'mapping'],
    path: /^\/chemistry(?:\/(?:files|projects))?\/?$/,
    steps: [
      { title: 'Start the importer', body: 'Choose Upload in the left navigation.', targetText: 'Upload' },
      { title: 'Select a file', body: 'Drop or browse for CSV, XLSX, SDF, DWAR or another supported scientific format.' },
      { title: 'Review the preview', body: 'Check the detected rows and make sure the correct header row is being used.' },
      { title: 'Map identity and structure', body: 'Map a structure field such as SMILES, InChI or CAS, plus your compound identifier. Keep other columns as custom fields when appropriate.' },
      { title: 'Confirm units and destination', body: 'Review units, select the project, then import. Resolve warnings rather than silently changing scientific values.' },
    ],
  },
  {
    id: 'assay-workflow',
    title: 'Register and run an assay',
    summary: 'Define a protocol, create a run and upload results.',
    keywords: ['assay', 'protocol', 'run', 'results', 'echo', 'queue'],
    path: /^\/biology\/(assays|assay-queue)/,
    steps: [
      { title: 'Open Assays', body: 'Use the Assays area to register a reusable assay definition.', targetText: 'Assays' },
      { title: 'Record the protocol', body: 'Add the method, units, controls and protocol version. Attach the source document when available.' },
      { title: 'Create a run', body: 'Create a run against a specific protocol version and assign compounds or samples.' },
      { title: 'Upload partial or complete results', body: 'Import results as they arrive. Completed compounds remain linked to the run while pending compounds stay visible.' },
      { title: 'Review provenance', body: 'Check operator, date, protocol version, source file and compound-level results before completing the run.' },
    ],
  },
  {
    id: 'inventory',
    title: 'Add and maintain inventory',
    summary: 'Create stock, record locations and update physical amounts.',
    keywords: ['inventory', 'stock', 'sample', 'batch', 'location', 'amount'],
    path: /^\/inventory/,
    steps: [
      { title: 'Open Add to Stock', body: 'Choose Add to Stock from Company Inventory.', targetText: 'Add to Stock' },
      { title: 'Identify the material', body: 'Select an existing compound or describe an unregistered reagent or stock item.' },
      { title: 'Complete required fields', body: 'Enter the amount, unit, container and storage location. The submit button remains unavailable until required information is complete.' },
      { title: 'Maintain the stock', body: 'Use Edit stock to change amount, status, location, container or expiry while retaining the audit history.' },
    ],
  },
  {
    id: 'toolbox',
    title: 'Use specialist workbenches',
    summary: 'Find a workbench and understand its validation status.',
    keywords: ['toolbox', 'workbench', 'tool', 'hub'],
    path: /^\/hubs/,
    steps: [
      { title: 'Open Toolbox', body: 'Choose Toolbox from the main navigation.', targetText: 'Toolbox' },
      { title: 'Review status', body: 'Available tools can be opened. Beta tools are still under validation; unavailable tools remain disabled.' },
      { title: 'Open the workbench', body: 'Choose a tool to open it in its platform workspace. Review the tool-specific help before using customer data.' },
    ],
  },
];

const DEFAULT_GUIDE: HelpGuide = {
  id: 'platform-overview',
  title: 'Find your way around Adroit',
  summary: 'Use the main navigation, page actions and contextual help.',
  keywords: ['start', 'navigation', 'platform', 'help', 'how'],
  path: /.*/,
  steps: [
    { title: 'Choose a scientific area', body: 'Use the top navigation for Chemistry, Biologics, Assays, Genomics, Pipeline, Toolbox or ELN.' },
    { title: 'Use the left navigation', body: 'The icons provide fast access to projects, files, registry, inventory, searches and administration.' },
    { title: 'Open contextual help', body: 'Choose ? on any page. This drawer recommends a walkthrough for the page you are viewing.' },
  ],
};

export function guideForPath(pathname: string): HelpGuide {
  return HELP_GUIDES.find((guide) => guide.path.test(pathname)) || DEFAULT_GUIDE;
}

export function searchGuides(query: string, pathname: string): HelpGuide[] {
  const stopWords = new Set(['a', 'an', 'and', 'do', 'how', 'i', 'is', 'it', 'me', 'the', 'to']);
  const terms = query.toLowerCase().trim().split(/[^a-z0-9]+/)
    .filter((term) => term.length > 1 && !stopWords.has(term));
  if (terms.length === 0) {
    const contextual = guideForPath(pathname);
    return [contextual, ...HELP_GUIDES.filter((guide) => guide.id !== contextual.id)].slice(0, 5);
  }
  return [...HELP_GUIDES, DEFAULT_GUIDE]
    .map((guide) => {
      const haystack = [guide.title, guide.summary, ...guide.keywords, ...guide.steps.map((step) => `${step.title} ${step.body}`)]
        .join(' ')
        .toLowerCase();
      return { guide, score: terms.reduce((total, term) => total + (haystack.includes(term) ? 1 : 0), 0) };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ guide }) => guide)
    .slice(0, 5);
}
